import { CHECKIN_WINDOW_MIN } from './checkins'
import { PER_DAY_PERIODS, isDueOn } from './schedule'

// The Upcoming tab's timeline: events, coach check-ins, dated tasks and the next
// days of recurring tasks, merged per local day. Shared by web and native.

export type UpcomingEvent = { _id: string; startsAt: number; endsAt: number; allDay: boolean }
export type UpcomingDue = {
  _id: string
  dueDate: number
  allDay: boolean
  completedAt: number | null
}
export type UpcomingRepeat = {
  _id: string
  recursPeriod: string | null
  recursDays: number[] | null
  recurrenceDay: number | null
  recurrenceTime: string | null
  allDay: boolean
  completedAt: number | null
}

export type UpcomingItem<E, D, R> =
  | { kind: 'event'; at: number; untimed: boolean; event: E }
  | { kind: 'call'; at: number; untimed: false }
  | { kind: 'task'; at: number; untimed: boolean; task: D }
  // Only the occurrence that stands for the task's current state can be ticked.
  | { kind: 'repeat'; at: number; untimed: boolean; task: R; day: string; tickable: boolean }

export type UpcomingDay<E, D, R> = { key: string; start: number; items: UpcomingItem<E, D, R>[] }

const MIN = 60_000
const CALL_MS = CHECKIN_WINDOW_MIN * MIN

/**
 * Days among `keys` a recurring task shows on. Daily-style schedules show only
 * the next open day (plus today when it's already done) so they don't fill
 * every row; weekly and monthly ones show each day in the window.
 */
export function repeatDays(
  task: Pick<UpcomingRepeat, 'recursPeriod' | 'recursDays' | 'recurrenceDay'>,
  keys: readonly string[],
  doneKey: string | null,
): string[] {
  const days = keys.filter((k) => isDueOn(task, k))
  if (!PER_DAY_PERIODS.has(task.recursPeriod ?? 'daily')) return days
  const open = days.findIndex((k) => k !== doneKey)
  return open < 0 ? days : days.slice(0, open + 1)
}

const minutesOf = (hhmm: string) => {
  const [h = 0, m = 0] = hhmm.split(':').map(Number)
  return h * 60 + m
}

// All-day events, then untimed tasks, then everything with a time.
const rank = (i: UpcomingItem<unknown, unknown, unknown>) =>
  i.kind === 'event' && i.untimed ? 0 : i.untimed ? 1 : 2

export function buildUpcoming<
  E extends UpcomingEvent,
  D extends UpcomingDue,
  R extends UpcomingRepeat,
>({
  days,
  keyOf,
  events = [],
  calls = [],
  dueTasks = [],
  overdue = [],
  repeating = [],
}: {
  // Local days of the window, in order; the first is today.
  days: readonly { key: string; start: number }[]
  keyOf: (ms: number) => string
  events?: readonly E[]
  calls?: readonly number[]
  dueTasks?: readonly D[]
  overdue?: readonly D[]
  repeating?: readonly R[]
}): { overdue: D[]; days: UpcomingDay<E, D, R>[] } {
  const from = days[0]?.start ?? 0
  const todayKey = days[0]?.key
  // Done tasks drop out, except ones ticked today, which stay struck through.
  const shown = (t: { completedAt: number | null }) =>
    t.completedAt == null || t.completedAt >= from
  const out = days.map((d) => ({
    key: d.key,
    start: d.start,
    items: [] as UpcomingItem<E, D, R>[],
  }))
  const byKey = new Map(out.map((d) => [d.key, d]))
  const keys = out.map((d) => d.key)

  for (const event of events)
    byKey
      .get(keyOf(Math.max(event.startsAt, from)))
      ?.items.push({ kind: 'event', at: event.startsAt, untimed: event.allDay, event })
  for (const at of calls) byKey.get(keyOf(at))?.items.push({ kind: 'call', at, untimed: false })
  for (const task of dueTasks)
    if (shown(task))
      byKey
        .get(keyOf(task.dueDate))
        ?.items.push({ kind: 'task', at: task.dueDate, untimed: task.allDay, task })
  for (const task of repeating) {
    const doneKey = task.completedAt == null ? null : keyOf(task.completedAt)
    repeatDays(task, keys, doneKey).forEach((key, i) => {
      const day = byKey.get(key)!
      const untimed = task.allDay || !task.recurrenceTime
      day.items.push({
        kind: 'repeat',
        at: day.start + (untimed ? 0 : minutesOf(task.recurrenceTime!) * MIN),
        untimed,
        task,
        day: key,
        tickable: key === todayKey || (i === 0 && task.completedAt == null),
      })
    })
  }
  for (const d of out) d.items.sort((a, b) => rank(a) - rank(b) || a.at - b.at)
  return { overdue: overdue.filter(shown), days: out }
}

/** Whether an item is done: a past event or check-in, or a ticked task. */
export function upcomingItemDone(
  item: UpcomingItem<UpcomingEvent, UpcomingDue, UpcomingRepeat>,
  now: number,
  keyOf: (ms: number) => string,
): boolean {
  switch (item.kind) {
    case 'event':
      return item.event.endsAt <= now
    case 'call':
      return item.at + CALL_MS <= now
    case 'task':
      return item.task.completedAt != null
    case 'repeat':
      return item.task.completedAt != null && keyOf(item.task.completedAt) === item.day
  }
}

/** Header count: what's still ahead in the window (overdue and Later excluded). */
export function upcomingCount(
  days: readonly UpcomingDay<UpcomingEvent, UpcomingDue, UpcomingRepeat>[],
  now: number,
  keyOf: (ms: number) => string,
): number {
  return days.reduce(
    (n, d) => n + d.items.filter((i) => !upcomingItemDone(i, now, keyOf)).length,
    0,
  )
}
